import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

import {
  AccessTypeEnum,
  SortOrder,
  useInfiniteModelPhotosQuery,
} from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

const {
  features: {
    model: { itemsPerQuery },
  },
} = getPublicConfig();

export const useModelPhotoList = (
  modelUserId: string,
  accessType: AccessTypeEnum,
) => {
  const { push } = useRouter();

  const {
    data: { pages } = {},
    isLoading,
    isFetching,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteModelPhotosQuery(
    "where",
    {
      where: { modelPhotoUserId: { equals: modelUserId } },
      take: itemsPerQuery,
      orderBy: [{ createdAt: SortOrder.Desc }, { id: SortOrder.Desc }],
      accessType,
    },
    {
      enabled: !!modelUserId,
      getNextPageParam: (lastPage) => {
        if ((lastPage?.modelPhotos?.length ?? 0) < itemsPerQuery) {
          return false;
        }

        return {
          cursor: {
            id: lastPage.modelPhotos?.[lastPage.modelPhotos.length - 1]?.id,
          },
        };
      },
    },
  );

  const fetchMore = () => {
    if (hasNextPage && !error) fetchNextPage();
  };

  const modelPhotos = useMemo(
    () => pages?.flatMap((page) => page?.modelPhotos) ?? [],
    [pages],
  );

  const emptyModelPhoto = useMemo(
    () => !isLoading && !modelPhotos?.length,
    [isLoading, modelPhotos],
  );

  useEffect(() => {
    if ((error as Error)?.message === "ACCESS_DENIED") push("/");
  }, [error]);

  return {
    modelPhotos,
    emptyModelPhoto,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    fetchMore,
    refetch,
  };
};
